import React, { useState } from "react";
import { SendIcon, Loader2 } from "lucide-react";
import { usePostAuth } from "../../../hooks/usePostAuth";
import { useQueryClient } from "@tanstack/react-query";
import Toast from "../../../components/Toast";

interface ResendButtonProps {
  invitationId: number;
}

export const ResendButton: React.FC<ResendButtonProps> = ({ invitationId }) => {
  const queryClient = useQueryClient();

  const { mutate, isPending } = usePostAuth<unknown, undefined>(
    `invitations/${invitationId}/resend`,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["get-invitations"] });
        setToast({
          type: "success",
          message: "Invitation resent successfully!",
        });
      },
      onError: (err: any) => {
        const msg =
          err?.message && typeof err.message === "string"
            ? err.message
            : "Failed to resend invitation.";
        setToast({ type: "error", message: msg });
      },
    }
  );

  const [toast, setToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const handleClick = () => {
    if (isPending) return;
    mutate(undefined);
  };

  return (
    <>
      <button
        onClick={handleClick}
        disabled={isPending}
        className={`
          inline-flex items-center gap-1 px-3 py-2 
          bg-blue-500 hover:bg-blue-600 disabled:bg-blue-400 
          text-white rounded-md text-sm font-medium 
          transition-colors duration-200
        `}
        title="Resend Invitation"
      >
        {isPending ? (
          <Loader2 className="animate-spin" size={16} />
        ) : (
          <SendIcon size={16} />
        )}
        <span className="hidden sm:inline">
          {isPending ? "Sending…" : "Resend"}
        </span>
      </button>

      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
